<template>
  <div class="form-group mb-3">
    <label v-if="label" :class="{ 'text-danger': hasError }">
      {{ label }}
    </label>
    <select
      :class="[inputClass, 'form-control flex-grow-1 w-100 custom-input shadow_inset_sm', { 'is-invalid': hasError }]"
      :value="value"
      :disabled="disabled"
      :style="selectStyle"
      v-bind="$attrs"
      @change="onChange"
      @blur="onBlur"
    >
      <option v-if="placeholder" value="" disabled selected>{{ placeholder }}</option>
      <option 
        v-for="(option, index) in normalizedOptions" 
        :key="index" 
        :value="option.value"
        :disabled="option.disabled"
      >
        {{ option.label }}
      </option>
    </select>
    <small v-if="hasError" class="form-text text-danger">
      {{ errorMessage }}
    </small>
  </div>
</template>
<script>
/**
 * /--------------------------/
 * COMPONENTE: SelectProvider 
 * /--------------------------/
 * 
 * Este Componente es un Select modularizado que soporta:
 * - Opciones dinámicas
 * - Arrays simples o de objetos
 * - Validaciones visuales de Bootstrap
 * 
 * --- INSTRUCCIONES DE USO POR PROP ---
 * 
 * @prop {Array} options
 *       Uso: `:options="['Opción 1', 'Opción 2']"` o `:options="[{id: 1, name: 'A'}, {id: 2, name: 'B'}]"`
 * 
 * @prop {String} optionValue
 *       Uso: `:optionValue="'id'"` (Default: 'id') - Llave del objeto a usar como valor
 * 
 * @prop {String|Function} optionLabel
 *       Uso: `:optionLabel="'name'"` o `:optionLabel="opt => \`\${opt.id} - \${opt.name}\`"` (Default: 'nombre')
 */

export default {
  name: 'SelectProvider',
  props: {
    // Texto del label
    label: { 
      type: String, 
      default: '' 
    },
    // Valor para v-model
    value: { 
      type: [String, Number, Boolean], 
      default: '' 
    },
    // Opciones del select
    options: { 
      type: Array, 
      required: true 
    },
    // Llave a usar como valor si la opción es objeto
    optionValue: { 
      type: String, 
      default: 'id' 
    },
    // Llave o función a usar como etiqueta si la opción es objeto
    optionLabel: { 
      type: [String, Function], 
      default: 'nombre' 
    },
    // Placeholder inicial
    placeholder: { 
      type: String, 
      default: '- Seleccione -' 
    },
    // Clases adicionales
    inputClass: { 
      type: [String, Object, Array], 
      default: '' 
    },
    // Atributos de estado
    disabled: { 
      type: Boolean, 
      default: false 
    },
    // Control de errores
    hasError: { 
      type: Boolean, 
      default: false 
    },
    errorMessage: { 
      type: String, 
      default: '' 
    },
    // Estilos personalizados específicos para la etiqueta <select>
    selectStyle: {
      type: [String, Object, Array],
      default: ''
    }
  },
  computed: {
    normalizedOptions() {
      return this.options.map(opt => {
        if (typeof opt === 'object' && opt !== null) {
          let label = '';
          if (typeof this.optionLabel === 'function') {
            label = this.optionLabel(opt);
          } else {
            label = opt[this.optionLabel];
          }
          return {
            value: opt[this.optionValue],
            label: label,
            disabled: opt.disabled || false
          };
        }
        return {
          value: opt,
          label: opt,
          disabled: false
        };
      });
    }
  },
  methods: {
    /**
     * Emite el valor al padre para mantener el v-model sincronizado
     */
    onChange(event) {
      this.$emit('input', event.target.value);
      this.$emit('change', event.target.value);
    },
    /**
     * Emite el evento cuando pierde el foco
     */
    onBlur(event) {
      this.$emit('blur', event.target.value);
    }
  },
  inheritAttrs: false
}
</script>
<style scoped>
  .custom-input {
    border: 1px solid #89898995;
    border-radius: 25px !important;
    padding: 12px 15px !important;
    height: auto !important;
    text-align: center;
    background-color: transparent;
    color: #45505e;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2345505e' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right 15px center;
    background-size: 16px;
    cursor: pointer;
  }

  .custom-input:focus {
    outline: none;
    box-shadow: none;
  }

  .custom-input:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
</style>
